'use client';

import type { RequiredSessionData, SessionData } from '@pickleballinc/lib/session';
import { createContext, type ReactNode, useContext, useMemo } from 'react';

interface SessionContextValue {
	session?: SessionData;
}

interface RequiredSessionContextValue {
	session: RequiredSessionData;
}

const SessionContext = createContext<SessionContextValue>({});

const hasUser = (value: SessionContextValue): value is RequiredSessionContextValue => !!value.session?.user;

interface SessionProviderProps {
	children: ReactNode;
	/** From `getSession()` / `getRequiredSession()` in `@pickleballinc/lib/session/server`, passed as is. */
	session: SessionData;
}

/** Makes the server session available to client components. Mount once, in the root (or secure) layout. */
export const SessionProvider = ({ children, session }: SessionProviderProps) => {
	const value = useMemo(() => ({ session }), [session]);

	return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

/** `session.user` is undefined when logged out. */
export const useSession = (): SessionContextValue => useContext(SessionContext);

/**
 * For login-protected apps (manage), where the provider is only mounted with a logged-in user.
 * Throws otherwise. Returns the context value itself, so it is referentially stable across renders.
 */
export const useRequiredSession = (): RequiredSessionContextValue => {
	const value = useContext(SessionContext);

	if (!hasUser(value)) {
		throw new Error('useRequiredSession must be used under a SessionProvider with a logged-in user');
	}

	return value;
};
